import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Trip } from '@/types/trip';
import { toast } from "sonner";

export const useTrips = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user.id) {
        throw new Error('No authenticated user');
      }

      const { data, error } = await supabase
        .from('trips')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTrips(data || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error('Failed to fetch trips');
    } finally {
      setLoading(false);
    }
  };

  const createTrip = async (name: string, budget: number) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user.id) {
        throw new Error('No authenticated user');
      }

      const { data, error } = await supabase
        .from('trips')
        .insert({
          name,
          budget,
          created_by: session.session.user.id
        })
        .select()
        .single();

      if (error) throw error;
      
      // Also add the creator as a participant
      const { error: participantError } = await supabase
        .from('trip_participants')
        .insert({
          trip_id: data.id,
          user_id: session.session.user.id
        });

      if (participantError) throw participantError;
      
      setTrips(prev => [data, ...prev]);
      toast.success(`Trip created successfully! Share ID: ${data.share_id}`);
      return data;
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error('Failed to create trip');
      return null;
    }
  };

  const joinTrip = async (shareId: string) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user.id) {
        throw new Error('No authenticated user');
      }

      // First, get the trip details
      const { data: trip, error: tripError } = await supabase
        .from('trips')
        .select('*')
        .eq('share_id', shareId)
        .single();

      if (tripError) throw tripError;

      // Then join the trip
      const { error: joinError } = await supabase
        .from('trip_participants')
        .insert({
          trip_id: trip.id,
          user_id: session.session.user.id
        });

      if (joinError) throw joinError;

      await fetchTrips(); // Refresh trips list
      toast.success('Successfully joined the trip!');
    } catch (error) {
      console.error('Error joining trip:', error);
      toast.error('Failed to join trip. Please check the Share ID.');
    }
  };

  return {
    trips,
    loading,
    createTrip,
    joinTrip
  };
};